import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  GraduationCap,
  ArrowRight,
  Clock,
  FileText,
  ShieldCheck,
  Ship,
  Building2,
  Calculator,
  Sparkles,
  Search,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema } from '../utils/seoSchemas';
import { knowledgeGuides, knowledgeCategories } from '../data/knowledgeAcademy';

// Category icon mapping
const CAT_ICON = {
  'VAT': Calculator,
  'Corporate Tax': FileText,
  'Customs Duty': Ship,
  'goAML / AML': ShieldCheck,
  'Free Zone': Building2
};

// Static "Explore" category cards (independent of the guides grid — evergreen category-level entry points)
const EXPLORE_CARDS = [
  {
    key: 'vat',
    category: 'VAT',
    icon: Calculator,
    title: 'VAT in UAE',
    description:
      'Understand VAT registration, return filing, input tax recovery, reverse charge mechanism, imports and exports, tax invoices, common mistakes, and practical compliance guidance.',
    cta: 'Explore VAT Guides',
    href: '/knowledge-academy/uae-vat-guide-2026'
  },
  {
    key: 'ct',
    category: 'Corporate Tax',
    icon: FileText,
    title: 'UAE Corporate Tax',
    description:
      'Learn about Corporate Tax registration, taxable income, Small Business Relief, Free Zone taxation, tax returns, transfer pricing, deductible expenses, and compliance obligations.',
    cta: 'Explore Corporate Tax Guides',
    href: '/knowledge-academy/uae-corporate-tax-guide-2026'
  },
  {
    key: 'customs',
    category: 'Customs Duty',
    icon: Ship,
    title: 'UAE Customs Duty',
    description:
      'Learn how customs duty is calculated, understand HS Codes, import procedures, customs documentation, CIF valuation, Free Zone movements, and customs compliance requirements.',
    cta: 'Explore Customs Guides',
    href: '/knowledge-academy/uae-customs-duty-guide'
  },
  {
    key: 'aml',
    category: 'goAML / AML',
    icon: ShieldCheck,
    title: 'UAE goAML & AML Compliance',
    description:
      'Understand goAML registration, Customer Due Diligence (CDD), Enhanced Due Diligence (EDD), Suspicious Transaction Reports (STR), AML policies, risk assessments, and internal audit requirements.',
    cta: 'Explore AML Guides',
    href: '/knowledge-academy/uae-goaml-aml-compliance-guide'
  }
];

const POPULAR_SEARCHES = [
  'VAT Registration',
  'Corporate Tax Return',
  'Small Business Relief',
  'Input VAT Recovery',
  'Customs Duty',
  'HS Code',
  'Reverse Charge Mechanism',
  'goAML Registration'
];

const KnowledgeAcademy = () => {
  usePageSeo({
    title: 'WPAA Knowledge Academy | UAE Tax & Compliance Learning Hub',
    description:
      'Practical guidance to help UAE businesses understand VAT, Corporate Tax, Customs Duty, AML compliance and financial reporting by experienced professionals.',
    path: '/knowledge-academy'
  });

  const publishedGuides = useMemo(
    () => knowledgeGuides.filter((g) => g.status === 'published'),
    []
  );

  const [query, setQuery] = useState('');

  const filteredGuides = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return publishedGuides;
    return publishedGuides.filter((g) => {
      const haystack = [g.title, g.subtitle, g.category, g.excerpt, ...(g.tags || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [publishedGuides, query]);

  const filteredExplore = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return EXPLORE_CARDS;
    return EXPLORE_CARDS.filter((c) =>
      [c.title, c.category, c.description].join(' ').toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="min-h-screen" data-testid="knowledge-academy-hub">
      <JsonLd
        id="ld-ka-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'WPAA Knowledge Academy', path: '/knowledge-academy' }
        ])}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full filter blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400 rounded-full filter blur-3xl" />
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <GraduationCap className="text-teal-400" size={32} />
              <span className="text-teal-400 font-semibold text-sm uppercase tracking-wider">
                WPAA Knowledge Academy
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-testid="ka-hero-title">
              WPAA Knowledge Academy
            </h1>
            <p className="text-xl md:text-2xl text-teal-200 mb-6 font-semibold leading-snug" data-testid="ka-hero-subtitle">
              Practical guidance to help UAE businesses understand VAT, Corporate Tax, Customs Duty, AML compliance and financial reporting by experienced professionals.
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              Whether you&apos;re starting a business, managing day-to-day compliance, or preparing for an audit, the WPAA Knowledge Academy provides reliable, practical resources to help you make informed decisions. Our guides are designed for business owners, finance professionals, accountants, entrepreneurs and anyone seeking a clearer understanding of UAE tax and compliance requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href="#explore-guides">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-400 text-white px-8" data-testid="ka-hero-cta-primary">
                  Explore Our Guides <ArrowRight size={16} className="ml-2" />
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8" data-testid="ka-hero-cta-secondary">
                  Contact Our Advisors
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES BAR */}
      <section className="py-8 px-4 bg-white border-b border-gray-100">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {knowledgeCategories.map((cat) => {
              const Icon = CAT_ICON[cat] || BookOpen;
              return (
                <div key={cat} className="flex items-center gap-2 text-sm font-medium text-gray-600">
                  <Icon size={16} className="text-teal-600" />
                  <span>{cat}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED GUIDES GRID */}
      <section className="py-20 px-4 bg-gray-50" id="featured-guides">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Latest Guides</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Featured Learning Guides</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Deep, evergreen guides on the topics UAE business owners ask us about most often.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ka-featured-grid">
            {filteredGuides.map((guide) => {
              const Icon = CAT_ICON[guide.category] || BookOpen;
              return (
                <Link
                  key={guide.slug}
                  to={`/knowledge-academy/${guide.slug}`}
                  data-testid={`ka-guide-${guide.slug}`}
                  className="group"
                >
                  <Card className="border-2 border-gray-100 h-full hover:border-teal-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                          <Icon size={20} className="text-teal-600" />
                        </div>
                        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">
                          {guide.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-700 transition-colors">
                        {guide.title}
                      </h3>
                      {guide.subtitle && (
                        <p className="text-sm text-teal-700 font-medium mb-3">{guide.subtitle}</p>
                      )}
                      <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
                        {guide.excerpt}
                      </p>
                      <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Clock size={12} />
                          <span>{guide.readingTime}</span>
                        </div>
                        <span className="text-sm font-semibold text-teal-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read guide <ArrowRight size={14} />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}

            {filteredGuides.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 text-center text-gray-500 py-10">
                No guides match &quot;{query}&quot;. Try a different search term or clear the filter.
              </div>
            )}

            {/* Coming soon placeholder */}
            {filteredGuides.length > 0 && (
              <Card className="border-2 border-dashed border-gray-200 bg-white/50">
                <CardContent className="p-6 flex flex-col items-center justify-center text-center h-full min-h-[240px]">
                  <Sparkles size={28} className="text-teal-500 mb-3" />
                  <h3 className="text-base font-semibold text-gray-700 mb-1">More Guides Coming</h3>
                  <p className="text-sm text-gray-500">
                    New guides on Free Zones, financial reporting and industry-specific compliance are in the pipeline.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* EXPLORE THE KNOWLEDGE ACADEMY */}
      <section className="py-20 px-4 bg-white border-t border-gray-100" id="explore-guides" data-testid="ka-explore-section">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Categories</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Explore the Knowledge Academy
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Browse our growing collection of practical guides covering the most important areas of UAE
              business compliance. Each guide is designed to answer common questions, explain complex
              regulations in simple language, and provide practical examples to support better business
              decisions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {filteredExplore.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.key}
                  to={card.href}
                  className="group"
                  data-testid={`ka-explore-${card.key}`}
                >
                  <Card className="border-2 border-gray-100 h-full hover:border-teal-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6 md:p-7 flex flex-col h-full">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center">
                          <Icon size={22} className="text-teal-700" />
                        </div>
                        <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">
                          {card.category}
                        </span>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 group-hover:text-teal-700 transition-colors">
                        {card.title}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-5 flex-1">
                        {card.description}
                      </p>
                      <span className="text-sm font-semibold text-teal-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                        {card.cta} <ArrowRight size={14} />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
            {filteredExplore.length === 0 && (
              <div className="md:col-span-2 text-center text-gray-500 py-10">
                No categories match &quot;{query}&quot;. Try a different search term.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="py-16 px-4 bg-gray-50 border-t border-gray-100" id="ka-search-section">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Looking for something specific?</h2>
          <p className="text-gray-600 mb-6">
            Search our Knowledge Academy to quickly find answers to your VAT, Corporate Tax, Customs Duty,
            AML and accounting questions.
          </p>

          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search size={18} className="text-gray-400" />
            </div>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the Knowledge Academy"
              className="w-full h-14 pl-12 pr-12 rounded-2xl border-2 border-gray-200 bg-white text-base text-gray-800 shadow-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition"
              data-testid="ka-search-input"
              aria-label="Search the Knowledge Academy"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-700"
                aria-label="Clear search"
                data-testid="ka-search-clear"
              >
                <X size={18} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mr-1">
              Popular:
            </span>
            {POPULAR_SEARCHES.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => setQuery(term)}
                className="text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-100 px-3 py-1.5 rounded-full transition"
                data-testid={`ka-popular-${term.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Who this is for</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-3">Written for UAE business owners</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Not accountants writing for other accountants; we answer the questions our clients actually ask.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { label: 'Business owners', icon: Building2 },
              { label: 'SMEs & start-ups', icon: Sparkles },
              { label: 'Finance teams', icon: Calculator },
              { label: 'Compliance officers', icon: ShieldCheck }
            ].map((who) => {
              const Icon = who.icon;
              return (
                <div
                  key={who.label}
                  className="bg-gray-50 rounded-xl p-5 text-center border border-gray-100 hover:border-teal-500 transition-all"
                >
                  <Icon size={26} className="text-teal-600 mx-auto mb-2" />
                  <p className="font-semibold text-gray-800 text-sm">{who.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-700 to-emerald-700 text-white">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">Have a specific question?</h2>
          <p className="text-lg text-teal-100 mb-6">
            Our guides cover the fundamentals. For anything specific to your business, book a free
            consultation with our tax team.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-teal-700 hover:bg-gray-100 px-8" data-testid="ka-cta-consultation">
                Book Free Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="ka-cta-whatsapp">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeAcademy;
